import {
  createToaster,
  Toaster as ChakraToaster,
  Toast,
  Stack,
  Box,
} from "@chakra-ui/react";

/**
 * Single source of truth for transient request feedback.
 *
 * Use `notify()` for any short-lived async confirmation:
 *   notify({ type: "success", title: "Widget saved" })
 *   notify({ type: "error",   title: "Couldn't save", description: err.message })
 *
 * For persistent contextual state (empty states, long-running jobs, fatal page
 * errors with retry), keep using inline <InfoBox>. Toasts are NOT for state —
 * they're for "this thing just happened, you can move on."
 *
 * Soft-editorial visuals:
 *  - Tinted wash background per type (mapped to brand accent tokens)
 *  - 4px left rule in the matching accent
 *  - Geist Mono UPPERCASE pre-header showing type
 *  - Geist body for the message
 *  - Soft popover shadow, 14px radius
 *  - Errors stay 8s with a manual close; success/info disappear at 4s; warning 6s
 */

export type NotifyType = "success" | "error" | "warning" | "info";

const durations: Record<NotifyType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
};

// Chakra's toaster `type` controls the semantic role; we use it to pick the
// accent token at render time. `success`/`error`/`warning`/`info` is the same
// string set Chakra ships with so its built-in role mapping still works.
export const toaster = createToaster({
  placement: "top-end",
  pauseOnPageIdle: true,
  duration: 4000,
});

interface NotifyOptions {
  type?: NotifyType;
  title: string;
  description?: string;
  /** Override default duration (ms). Falls back to type-based default. */
  duration?: number;
  /** Set false to opt out of the close button. */
  closable?: boolean;
}

const accentRoleFor = (type: NotifyType): string => {
  switch (type) {
    case "success":
      return "healthy";
    case "error":
      return "risk";
    case "warning":
      return "warning";
    case "info":
    default:
      return "info";
  }
};

export const notify = ({
  type = "info",
  title,
  description,
  duration,
  closable,
}: NotifyOptions) => {
  return toaster.create({
    type,
    title,
    description,
    duration: duration ?? durations[type],
    closable: closable ?? type === "error",
    meta: { accent: accentRoleFor(type) },
  });
};

// Backwards-compatible aliases so legacy call sites keep working while the
// codebase migrates to `notify()`. Both forward to the same toaster.
export const toast = ({
  title,
  description,
  type = "info",
}: {
  title: string;
  description?: string;
  type?: NotifyType;
}) => notify({ title, description, type });

export const Toaster = () => {
  return (
    <ChakraToaster toaster={toaster}>
      {(t: any) => {
        const accent =
          (t.meta as any)?.accent ?? accentRoleFor(t.type as NotifyType);
        const dot = `var(--accent-${accent}-rule)`;

        return (
          <Toast.Root
            bg="var(--chakra-colors-bg-panel)"
            color="var(--text-primary)"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="var(--card-border)"
            borderRadius="var(--radius-toast)"
            boxShadow="var(--shadow-popover)"
            px={4}
            py={3}
            minW="320px"
            maxW="420px"
          >
            <Stack direction="row" gap={3} flex={1} align="flex-start">
              {/* Small dot indicating the toast type */}
              <Box
                as="span"
                w="8px"
                h="8px"
                mt="6px"
                borderRadius="var(--radius-pill)"
                bg={dot}
                flexShrink={0}
              />
              <Stack gap={1} flex={1}>
                {t.title && (
                  <Toast.Title
                    fontFamily="var(--font-heading)"
                    fontSize="15px"
                    fontWeight={500}
                    letterSpacing="-0.01em"
                    lineHeight="1.3"
                  >
                    {t.title}
                  </Toast.Title>
                )}
                {t.description && (
                  <Toast.Description
                    fontSize="13px"
                    lineHeight="1.4"
                    color="var(--text-secondary-color)"
                  >
                    {t.description}
                  </Toast.Description>
                )}
              </Stack>
              {t.closable && <Toast.CloseTrigger />}
            </Stack>
          </Toast.Root>
        );
      }}
    </ChakraToaster>
  );
};
